import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Globe2, Map as MapIcon, Waves, AlertTriangle, SatelliteDish, Plane, Fish } from "lucide-react";
import { fetchOverview, fetchHistory, fetchFishingAdvisory } from "@/lib/api";
import GlobeView from "@/components/GlobeView";
import FlowMap from "@/components/FlowMap";
import OceanView from "@/components/OceanView";
import RiskTicker from "@/components/RiskTicker";
import BriefingPanel from "@/components/BriefingPanel";
import ControlBar from "@/components/ControlBar";
import Legend from "@/components/Legend";
import CoastDetail from "@/components/CoastDetail";
import MethodologyDialog from "@/components/MethodologyDialog";
import ScoreboardDialog from "@/components/ScoreboardDialog";
import FishingMap from "@/components/FishingMap";
import FishingPanel from "@/components/FishingPanel";
import Splash from "@/components/Splash";

function useAgo(iso) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  return s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ${s % 60}s ago`;
}

export default function Dashboard() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["overview"],
    queryFn: fetchOverview,
    refetchInterval: 60000,
  });
  const { data: history } = useQuery({
    queryKey: ["history"],
    queryFn: fetchHistory,
    staleTime: 15 * 60 * 1000,
  });

  const [tab, setTab] = useState("globe");
  const [mode, setMode] = useState("sargassum");
  const [placed, setPlaced] = useState(null);
  const [species, setSpecies] = useState(null);
  const queryClient = useQueryClient();
  const { data: advisory } = useQuery({
    queryKey: ["fishing-advisory", species],
    queryFn: () => fetchFishingAdvisory(species),
    enabled: mode === "fishing",
    refetchInterval: 120000,
  });
  const [selectedIsland, setSelectedIsland] = useState(null);
  const [playIdx, setPlayIdx] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [coastOpen, setCoastOpen] = useState(false);
  const [layers, setLayers] = useState({
    biomass: true,
    drift: true,
    flow: true,
    satellite: true,
    islands: true,
  });

  const ago = useAgo(data?.generated_at);

  // keep the branded splash up for a guaranteed minimum so people can actually see it
  const [minSplash, setMinSplash] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setMinSplash(false), 30000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (data?.islands?.length && !selectedIsland) {
      setSelectedIsland(data.islands[0].id);
    }
  }, [data, selectedIsland]);

  // unified timeline: real observed past days -> NOW (nowcast) -> ensemble forecast horizons
  const timeline = useMemo(() => {
    const past = (history?.frames || []).map((f) => ({
      label: f.date, kind: "obs", belt: f.belt, islands: f.islands, predicted_paths: f.predicted_paths,
    }));
    const fc = (data?.forecast?.horizons || [])
      .filter((h) => h.hour > 0)
      .map((h) => ({
        label: `+${h.hour}h`, kind: "forecast", belt: h.points,
        islands: data.islands, predicted_paths: data.predicted_paths,
      }));
    const now = {
      label: "NOW", kind: "now", belt: data?.belt || [],
      islands: data?.islands || [], predicted_paths: data?.predicted_paths || [],
    };
    if (past.length === 0 && !data) return [];
    return [...past.slice(0, -1), now, ...fc];
  }, [history, data]);
  const nowIndex = Math.max(0, (history?.frames?.length || 1) - 1);

  useEffect(() => {
    if (timeline.length && playIdx === null) setPlayIdx(nowIndex);
  }, [timeline, playIdx, nowIndex]);

  useEffect(() => {
    if (!playing || !timeline.length) return;
    const t = setInterval(() => {
      setPlayIdx((i) => ((i ?? 0) + 1 >= timeline.length ? 0 : (i ?? 0) + 1));
    }, 850);
    return () => clearInterval(t);
  }, [playing, timeline]);

  const selected = useMemo(
    () => data?.islands?.find((i) => i.id === selectedIsland) || null,
    [data, selectedIsland],
  );

  const isLive = playIdx === null || (playIdx === nowIndex && !playing);
  const mapData = useMemo(() => {
    const frame = timeline[playIdx ?? nowIndex];
    if (!frame || isLive) return data;
    return {
      bbox: data?.bbox || [-90, 7, -55, 26],
      belt: frame.belt,
      predicted_paths: frame.predicted_paths,
      islands: frame.islands,
      flowlines: data?.flowlines || [],
    };
  }, [timeline, playIdx, isLive, data, nowIndex]);

  const toggle = (k) => setLayers((l) => ({ ...l, [k]: !l[k] }));
  const stats = data?.stats;

  const openCoast = (id) => {
    if (id) setSelectedIsland(id);
    setCoastOpen(true);
  };

  return (
    <div className="flex h-screen flex-col text-slate-100">
      {(!data || minSplash) && <Splash />}
      {/* header */}
      <header className="glass-solid z-50 flex min-h-[76px] shrink-0 items-center justify-between border-b border-cyan-500/20 px-4 py-2 sm:px-6">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-start leading-none">
            <img
              src="/brand/sargasentry-dark.webp"
              alt="SargaSentry — Caribbean Early Warning"
              className="h-11 w-auto sm:h-14"
              data-testid="app-logo"
            />
            <div
              className="mt-1.5 hidden font-mono-tel text-[10px] font-semibold tracking-[0.22em] text-cyan-400/80 sm:block sm:text-[11px]"
              data-testid="app-tagline"
            >
              SEE IT. TRACK IT. ACT EARLY.
            </div>
          </div>
          <div className="ml-1 flex items-center gap-1 rounded-lg border border-white/10 bg-black/40 p-1">
            <ModeBtn active={mode === "sargassum"} onClick={() => setMode("sargassum")} icon={Waves} label="Sargassum" testid="mode-sargassum" />
            <ModeBtn active={mode === "fishing"} onClick={() => setMode("fishing")} icon={Fish} label="Fishing" testid="mode-fishing" />
          </div>
        </div>

        <div className="hidden items-center gap-5 md:flex">
          <Stat icon={SatelliteDish} label="Belt extent" value={stats ? `${stats.belt_extent_mt} Mt` : "—"} />
          <Stat
            icon={AlertTriangle}
            label="High-risk"
            value={stats ? `${stats.islands_high}/${stats.islands_total}` : "—"}
            danger={stats?.islands_high > 0}
          />
          <Stat icon={Activity} label="Wind data" value={data?.wind_source?.replace(" (live)", "") || "—"} />
        </div>

        <div className="flex items-center gap-2">
          <span className="live-dot h-2 w-2 rounded-full bg-emerald-400" />
          <div className="text-right leading-none">
            <div className="font-mono-tel text-xs font-bold text-emerald-300">LIVE</div>
            <div className="eyebrow text-slate-400">{ago}</div>
          </div>
        </div>
      </header>

      {/* real-data provenance strip */}
      {mode === "sargassum" && data?.data_sources && (
        <div data-testid="data-sources" className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-white/5 bg-black/40 px-4 py-1.5 text-[11px] sm:px-6">
          <span className="inline-flex items-center gap-1.5 font-mono-tel font-bold text-emerald-400">
            <span className="live-dot h-1.5 w-1.5 rounded-full bg-emerald-400" /> REAL DATA
          </span>
          <SourceBadge label="Detection" name="USF/NOAA AFAI" time={data.data_sources.detection.obs_time} stale={data.data_sources.detection.stale} />
          <SourceBadge label="Currents" name="NOAA-AOML" time={data.data_sources.currents.obs_time} stale={data.data_sources.currents.stale} />
          <SourceBadge label="Wind" name={(data.wind_source || "").replace(" (live)", "")} />
          {data.forecast?.skill?.available && (
            <span className="inline-flex items-center gap-1.5" data-testid="model-skill">
              <span className="eyebrow text-slate-400">Belt-drift skill</span>
              <span className="font-mono-tel font-bold text-cyanx">
                {data.forecast.skill.skill_vs_persistence_pct >= 0 ? "+" : ""}{data.forecast.skill.skill_vs_persistence_pct}% vs persistence
              </span>
              <span className="font-mono-tel text-slate-400">
                ±{data.forecast.skill.position_error_km}km @{data.forecast.skill.lead_hours}h
              </span>
            </span>
          )}
          <span className="ml-auto hidden text-slate-400 lg:block">Nowcast = measured belt · ETA = physical drift projection (directional)</span>
          <ScoreboardDialog />
          <MethodologyDialog data={data} />
        </div>
      )}

      {/* body grid */}
      {mode === "sargassum" && (
      <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto lg:overflow-hidden mobile-scroll thin-scroll p-3 lg:grid-cols-12 lg:gap-4 lg:p-4">
        {/* left: risk ticker */}
        <aside className="order-2 h-[68vh] lg:h-full lg:order-1 lg:col-span-3 overflow-hidden">
          <RiskTicker islands={data?.islands || []} selectedIsland={selectedIsland} onSelect={setSelectedIsland} />
        </aside>

        {/* center: viz */}
        <main className="order-1 flex h-[60vh] flex-col gap-3 lg:order-2 lg:col-span-6 lg:h-full lg:min-h-0">
          <div className="glass relative flex-1 overflow-hidden rounded-xl">
            {/* tab switch */}
            <div className="absolute left-3 top-3 z-20 flex gap-1 rounded-lg border border-white/10 bg-black/40 p-1 backdrop-blur">
              <TabBtn active={tab === "globe"} onClick={() => setTab("globe")} icon={Globe2} label="3D Globe" testid="tab-globe" />
              <TabBtn active={tab === "flow"} onClick={() => setTab("flow")} icon={MapIcon} label="Flow Map" testid="tab-flow" />
              <TabBtn active={tab === "ocean"} onClick={() => setTab("ocean")} icon={Plane} label="Drone Ocean" testid="tab-ocean" />
            </div>
            <div className="pointer-events-none absolute right-3 top-3 z-20 hidden rounded-md border border-white/10 bg-black/40 px-2 py-1 backdrop-blur sm:block">
              <span className="eyebrow text-slate-400">
                {isLive
                  ? tab === "globe"
                    ? "Great Atlantic Sargassum Belt · 48h drift"
                    : tab === "flow"
                    ? "Caribbean · flow-lines + satellite"
                    : "Drone feed · real-time drift tracking"
                  : `Playback · ${history?.frames[playIdx]?.date || ""}`}
              </span>
            </div>

            <div className="h-full w-full">
              {tab === "globe" ? (
                <GlobeView
                  data={mapData}
                  layers={layers}
                  forecastHour={0}
                  selectedIsland={selectedIsland}
                  onSelectIsland={setSelectedIsland}
                />
              ) : tab === "flow" ? (
                <FlowMap
                  data={mapData}
                  layers={layers}
                  forecastHour={0}
                  selectedIsland={selectedIsland}
                  onSelectIsland={setSelectedIsland}
                />
              ) : (
                <OceanView
                  data={mapData}
                  layers={layers}
                  selectedIsland={selectedIsland}
                  onSelectIsland={setSelectedIsland}
                />
              )}
            </div>
            <div
              className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2"
              data-testid="map-hint"
            >
              <span className="rounded-full border border-cyan-400/30 bg-black/60 px-3 py-1 text-xs text-cyan-100/90 backdrop-blur">
                Tap an island for details
              </span>
            </div>
            <Legend mode={tab} />
          </div>

          <ControlBar
            layers={layers}
            onToggle={toggle}
            onRefresh={() => refetch()}
            refreshing={isFetching}
            frames={timeline}
            idx={playIdx ?? nowIndex}
            nowIndex={nowIndex}
            playing={playing}
            onScrub={(i) => {
              setPlaying(false);
              setPlayIdx(i);
            }}
            onPlayToggle={() => setPlaying((p) => !p)}
          />
        </main>

        {/* right: briefing */}
        <aside className="order-3 h-[72vh] lg:h-full lg:col-span-3 overflow-hidden">
          <BriefingPanel island={selected} onOpenCoastDetail={openCoast} />
        </aside>
      </div>
      )}

      {mode === "fishing" && (
        <div className="grid flex-1 grid-cols-1 gap-3 overflow-y-auto lg:overflow-hidden mobile-scroll thin-scroll p-3 lg:grid-cols-12 lg:gap-4 lg:p-4">
          <main className="order-1 h-[60vh] lg:col-span-8 lg:h-full lg:min-h-0">
            <div className="glass relative h-full overflow-hidden rounded-xl">
              <div className="absolute left-3 top-3 z-20 flex items-center gap-2 rounded-md border border-white/10 bg-black/45 px-2.5 py-1.5 backdrop-blur" data-testid="fishing-brand">
                <img src="/brand/mark.png" alt="SargaSentry" className="h-6 w-auto" />
                <div className="leading-none">
                  <div className="font-display text-xs font-extrabold tracking-tight text-white">SARGASENTRY</div>
                  <div className="eyebrow text-cyan-400/70">Fishing Advisory</div>
                </div>
              </div>
              <div className="pointer-events-none absolute right-3 top-3 z-20 hidden rounded-md border border-white/10 bg-black/40 px-2 py-1 backdrop-blur sm:block">
                <span className="eyebrow text-slate-400">Caribbean fishing grounds · bycatch-risk vs opportunity</span>
              </div>
              <FishingMap advisory={advisory} islands={data?.islands || []} placed={placed} onPlace={setPlaced} />
              <FishingLegend />
            </div>
          </main>
          <aside className="order-2 h-[72vh] lg:h-full lg:col-span-4 overflow-hidden">
            <FishingPanel
              advisory={advisory}
              placed={placed}
              onPlaced={setPlaced}
              species={species}
              onSpecies={setSpecies}
              onRefetch={() => queryClient.invalidateQueries({ queryKey: ["fishing-advisory"] })}
            />
          </aside>
        </div>
      )}

      <CoastDetail islandId={selectedIsland} open={coastOpen} onOpenChange={setCoastOpen} />
    </div>
  );
}

function Stat({ icon: Icon, label, value, danger }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className={`h-4 w-4 ${danger ? "text-amberx" : "text-cyan-400/70"}`} />
      <div className="leading-none">
        <div className="eyebrow text-slate-400">{label}</div>
        <div className={`font-mono-tel text-sm font-bold ${danger ? "text-amberx" : "text-slate-200"}`}>
          {value}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      aria-label={label}
      className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-md px-3 py-2 text-xs font-mono-tel font-semibold tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
        active ? "bg-cyan-500/20 text-cyan-100" : "text-slate-300 hover:text-white"
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function ModeBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      aria-label={label}
      className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-md px-3 py-2 text-xs font-mono-tel font-bold tracking-wide transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 ${
        active ? "bg-cyan-500/20 text-cyan-100 shadow-[0_0_12px_rgba(34,211,238,0.25)]" : "text-slate-300 hover:text-white"
      }`}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function FishingLegend() {
  const items = [
    { c: "#00ff87", label: "Prime fishing" },
    { c: "#34d399", label: "Good" },
    { c: "#FFD166", label: "Fair" },
    { c: "#ef4444", label: "Avoid (bycatch)" },
  ];
  return (
    <div className="pointer-events-none absolute bottom-2 right-2 z-20 rounded-md border border-white/10 bg-black/55 px-2.5 py-1.5 backdrop-blur">
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {items.map((i) => (
          <span key={i.label} className="inline-flex items-center gap-1.5 font-mono-tel text-[10px] text-slate-300">
            <span className="h-2 w-2 rounded-sm" style={{ background: i.c }} /> {i.label}
          </span>
        ))}
      </div>
    </div>
  );
}


function SourceBadge({ label, name, time, stale }) {
  const date = time ? String(time).slice(0, 10) : null;
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-400">
      <span className="eyebrow text-slate-400">{label}</span>
      <span className="font-mono-tel text-slate-300">{name}</span>
      {date && <span className="font-mono-tel text-slate-400">{date}</span>}
      {stale && (
        <span className="rounded bg-amber-500/15 px-1 font-mono-tel text-[10px] font-bold text-amberx">CACHED</span>
      )}
    </span>
  );
}
